import { useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdDownload, MdErrorOutline, MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { Appearance, Size } from '../../../Components/designConstants';
import { downloadCsvBlob } from '../CommerceScraperUtils';
import {
  PACVUE_MAX_ROWS_PER_FILE,
  PACVUE_SKIP_REASON_LABEL,
  PacvuePlannedFile,
  PacvueSkipReasonType
} from './pacvue-export-constants';
import {
  planPacvueExport,
  readPacvueConfig,
  renderPlannedFile,
  StoredPacvueConfig,
  writePacvueConfig
} from './pacvue-export-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { TitleRewriteStore } from './title-compliance-utils';

interface PacvueExportModalProps {
  products: AuditedProduct[];
  /** Saved title / item-highlight rewrites — the source of everything exported. */
  rewriteStore: TitleRewriteStore;
  onClose: () => void;
}

/**
 * Builds the Pacvue bulk-upload CSV(s) from saved rewrites.
 *
 * The preview is computed live from whatever is currently typed, so an operator
 * sees the row count, the file split and the full skip breakdown BEFORE
 * downloading anything. That matters because the downloaded file edits live
 * Amazon listings once someone uploads it — "how many rows and which ones" is
 * not a question to answer after the fact.
 */
export const PacvueExportModal = ({ products, rewriteStore, onClose }: PacvueExportModalProps) => {
  const [config, setConfig] = useState<StoredPacvueConfig>(readPacvueConfig);
  const [downloaded, setDownloaded] = useState<string[]>([]);

  // Planned, not rendered: the preview needs counts and file names, and
  // re-escaping every cell on each keystroke in the Account ID field would make
  // typing visibly laggy on a full catalog. CSV text is built on download.
  const result = useMemo(
    () => planPacvueExport(products, rewriteStore, config),
    [products, rewriteStore, config]
  );

  /** Skip reasons rolled up to counts, so 3,000 skipped rows read as four lines. */
  const skipSummary = useMemo(() => {
    const counts = new Map<PacvueSkipReasonType, number>();
    for (const row of result.skipped) {
      counts.set(row.reason, (counts.get(row.reason) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [result.skipped]);

  const update = (patch: Partial<StoredPacvueConfig>) =>
    setConfig(previous => ({ ...previous, ...patch }));

  const handleDownload = (file: PacvuePlannedFile) => {
    // Remember the identifiers only once the operator has committed to a
    // download — typing a wrong value and closing shouldn't persist it.
    writePacvueConfig(config);
    downloadCsvBlob(renderPlannedFile(file, config), file.fileName);
    setDownloaded(previous =>
      previous.includes(file.fileName) ? previous : [...previous, file.fileName]
    );
  };

  const handleDownloadAll = () => {
    for (const file of result.files) {
      handleDownload(file);
    }
  };

  const hasFiles = result.files.length > 0;
  const nothingToExport = result.errors.length === 0 && !hasFiles;

  return (
    <Modal show centered size='lg' onHide={onClose} className='scorecard-pacvue-export-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Pacvue Bulk Update</span>
          <Modal.Title>Export updated titles</Modal.Title>
          <span className='scorecard-modal-subtitle'>
            Builds the “Update Listing For Vendor” sheet Pacvue expects. Only the new title and item
            highlight are written — every other listing field is left blank so Pacvue leaves it
            untouched.
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className='scorecard-pacvue-fields'>
          <Form.Group>
            <Form.Label>
              Account ID <span className='scorecard-pacvue-required'>required</span>
            </Form.Label>
            <Form.Control
              type='text'
              value={config.accountId}
              placeholder='From Pacvue, per account'
              onChange={e => update({ accountId: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              Vendor Code <span className='scorecard-pacvue-required'>required</span>
            </Form.Label>
            <Form.Control
              type='text'
              value={config.vendorCode}
              placeholder='e.g. ABCDE'
              onChange={e => update({ vendorCode: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Market</Form.Label>
            <Form.Control
              type='text'
              value={config.market}
              onChange={e => update({ market: e.target.value })}
            />
          </Form.Group>
        </div>
        <p className='scorecard-pacvue-hint'>
          <MdInfoOutline /> Account ID and Vendor Code are remembered on this browser after your
          first download. Leaving them blank is what causes Pacvue to reject an upload.
        </p>

        {result.errors.length > 0 && (
          <div className='scorecard-pacvue-errors'>
            {result.errors.map(error => (
              <p key={error}>
                <MdErrorOutline /> {error}
              </p>
            ))}
          </div>
        )}

        {result.errors.length === 0 && (
          <div className='scorecard-pacvue-summary'>
            <div className='scorecard-pacvue-summary-figure'>
              <strong>{result.exportedCount.toLocaleString()}</strong>
              <span>ASIN{result.exportedCount === 1 ? '' : 's'} to update</span>
            </div>
            <div className='scorecard-pacvue-summary-figure'>
              <strong>{result.files.length.toLocaleString()}</strong>
              <span>
                file{result.files.length === 1 ? '' : 's'} · max{' '}
                {PACVUE_MAX_ROWS_PER_FILE.toLocaleString()} rows each
              </span>
            </div>
            <div className='scorecard-pacvue-summary-figure'>
              <strong>{result.skipped.length.toLocaleString()}</strong>
              <span>not included</span>
            </div>
          </div>
        )}

        {skipSummary.length > 0 && (
          <div className='scorecard-pacvue-skips'>
            <h4>
              <MdWarningAmber /> Left out of the export
            </h4>
            <ul>
              {skipSummary.map(([reason, count]) => (
                <li key={reason}>
                  <strong>{count.toLocaleString()}</strong> — {PACVUE_SKIP_REASON_LABEL[reason]}
                </li>
              ))}
            </ul>
          </div>
        )}

        {nothingToExport && (
          <div className='scorecard-modal-success-banner'>
            <MdInfoOutline />
            <p>Nothing to export yet — generate some titles or item highlights first.</p>
          </div>
        )}

        {hasFiles && (
          <div className='scorecard-pacvue-files'>
            {result.files.map(file => (
              <div key={file.fileName} className='scorecard-pacvue-file'>
                <div className='scorecard-pacvue-file-info'>
                  <span className='scorecard-pacvue-file-name'>{file.fileName}</span>
                  <span className='scorecard-pacvue-file-meta'>
                    {file.rowCount.toLocaleString()} ASIN{file.rowCount === 1 ? '' : 's'}
                    {downloaded.includes(file.fileName) && ' · downloaded'}
                  </span>
                </div>
                <Button
                  size={Size.SM}
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdDownload />}
                  onClick={() => handleDownload(file)}
                >
                  Download
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <p className='scorecard-modal-footnote'>
          Upload each file to Pacvue separately — one upload per file, in order.
        </p>
        {hasFiles && (
          <Button size={Size.LG} leadingElement={<MdDownload />} onClick={handleDownloadAll}>
            Download {result.files.length === 1 ? 'file' : `all ${result.files.length} files`}
          </Button>
        )}
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
